/**
 * Platform primary destinations — Home · Arena · Tournaments · Discover · Profile (ADR-030).
 * Session MatchApplication / Room Session Shell tabs are separate.
 * Activity (`/activity`) stays reachable via header notifications / Profile → Activity.
 * Route `/tournaments` remains the Tournaments directory (compatibility id `tournaments`).
 * Nav item id stays `me` for route matching stability; label is Profile.
 */
export const PLATFORM_NAV_ITEMS = Object.freeze([
  { id: "home", label: "Home", compactLabel: "Home", to: "/", icon: "home", match: ["/"] },
  {
    id: "arena",
    label: "Arena",
    compactLabel: "Arena",
    to: "/arena",
    icon: "zap",
    match: ["/arena", "/play", "/room"],
  },
  {
    id: "tournaments",
    label: "Tournaments",
    compactLabel: "Events",
    to: "/tournaments",
    icon: "trophy",
    match: ["/tournaments"],
  },
  {
    id: "discover",
    label: "Discover",
    compactLabel: "Discover",
    to: "/discover/tournaments",
    icon: "search",
    match: [
      "/discover",
      "/browse",
      "/players/search",
      "/rankings",
      "/organizations",
      "/organization",
    ],
  },
  {
    id: "me",
    label: "Profile",
    compactLabel: "Profile",
    to: "/profile",
    icon: "user",
    match: [
      "/profile",
      "/my-player",
      "/player",
      "/history",
      "/about",
      "/privacy",
      "/feedback",
      "/activity",
      "/friends",
      "/my-registrations",
      "/my-organization",
    ],
  },
]);

/**
 * Guest-safe chrome — Discover + Sign in only (no dead Home/Arena/Tournaments/Profile tabs).
 */
export const GUEST_PLATFORM_NAV_ITEMS = Object.freeze([
  {
    id: "discover",
    label: "Discover",
    compactLabel: "Discover",
    to: "/discover/tournaments",
    icon: "search",
    match: [
      "/discover",
      "/browse",
      "/rankings",
      "/organizations",
      "/organization",
      "/about",
      "/privacy",
    ],
  },
  {
    id: "sign-in",
    label: "Sign in",
    compactLabel: "Sign in",
    to: "/sign-in",
    icon: "user",
    match: ["/sign-in"],
  },
]);

/**
 * @param {boolean} isAuthenticated
 */
export function resolvePlatformNavItems(isAuthenticated) {
  return isAuthenticated ? PLATFORM_NAV_ITEMS : GUEST_PLATFORM_NAV_ITEMS;
}

/**
 * @param {string} pathname
 * @returns {string | null} nav item id
 */
export function resolveActiveNavId(pathname) {
  const path = pathname || "/";
  // Organizer Tournament Module under /arena/:id/tournament|live — Tournaments IA (RC1)
  if (/^\/arena\/[^/]+\/(tournament|live)(\/|$)/.test(path)) {
    return "tournaments";
  }
  // More specific prefixes first (avoid "/" swallowing everything)
  const ranked = [...PLATFORM_NAV_ITEMS, ...GUEST_PLATFORM_NAV_ITEMS]
    .filter((item, index, all) => all.findIndex((x) => x.id === item.id) === index)
    .sort((a, b) => maxPrefixLen(b.match) - maxPrefixLen(a.match));
  for (const item of ranked) {
    if (item.match.some((prefix) => pathMatches(path, prefix))) {
      return item.id;
    }
  }
  return null;
}

function maxPrefixLen(matches) {
  return Math.max(...matches.map((m) => m.length));
}

function pathMatches(path, prefix) {
  if (prefix === "/") return path === "/" || path === "";
  return path === prefix || path.startsWith(`${prefix}/`);
}
